/**
 * Ejecuta tareas con un maximo de N en paralelo y NUNCA rechaza.
 *
 * El reenvio hacia varios destinos lo necesita por dos motivos:
 *  - `Promise.all` rechaza al primer fallo, asi que un destino caido cancelaba
 *    los demas y el usuario no sabia cuales habian salido;
 *  - lanzar todo a la vez creaba tickets duplicados y mensajes desordenados.
 *
 * Devuelve un resultado por tarea, en el mismo orden de entrada.
 */
export type ResultadoTarea<T> =
  | { ok: true; valor: T }
  | { ok: false; error: unknown };

export async function settleWithConcurrency<T>(
  tareas: Array<() => Promise<T>>,
  limite = 4
): Promise<Array<ResultadoTarea<T>>> {
  const resultados: Array<ResultadoTarea<T>> = new Array(tareas.length);
  let siguiente = 0;

  const trabajador = async (): Promise<void> => {
    while (siguiente < tareas.length) {
      const indice = siguiente++;
      try {
        resultados[indice] = { ok: true, valor: await tareas[indice]() };
      } catch (error) {
        resultados[indice] = { ok: false, error };
      }
    }
  };

  const trabajadores = Array.from(
    { length: Math.max(1, Math.min(limite, tareas.length)) },
    () => trabajador()
  );

  await Promise.all(trabajadores);
  return resultados;
}

/**
 * Motivo legible de un fallo de axios.
 *
 * Algunos endpoints responden con `res.send("texto")` en vez de JSON, asi que
 * hay que mirar tambien el cuerpo en texto plano.
 */
export function motivoDeError(error: any): string {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.error) return String(data.error);
  if (data?.message) return String(data.message);
  if (error?.message) return String(error.message);
  return "error desconocido";
}
